import type { IconName } from 'lucide-react/dynamic';
import { DateTime } from 'luxon';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import {
  default as api,
  type Card,
  type Recurrence,
  type RecurrenceTaskItem,
  type RecurrenceTaskStatus,
  type User,
} from '../service/api';
import { createPersistStorage } from './utils/persist';

interface ManagerTaskItem {
  _id: string;
  status: RecurrenceTaskStatus;
  recurrenceId: string;
  title: string;
  description?: string;
  start: DateTime;
  stop: DateTime;
  rewardCard: Card | string;
  penaltyCard?: Card | string;
  assignedTo: User;
  icon: IconName;
  isCompleted: boolean;
}

interface UserTasks {
  user: User;
  tasks: ManagerTaskItem[];
}

interface ManagerTasksState {
  userTasks: UserTasks[];
  users: User[];
  selectedUserId: string | null;
  weekStart: DateTime;
  isLoading: boolean;
  error: string | null;
}

interface ManagerTasksActions {
  fetchTasks: (userId?: string) => Promise<void>;
  setSelectedUser: (userId: string | null) => void;
  setWeek: (weekStart: DateTime) => void;
  approveTask: (taskId: string, status: 'Succeed' | 'Failed') => Promise<void>;
  updateTaskStatus: (
    taskId: string,
    status: RecurrenceTaskStatus,
    recurrenceId: string,
    start: DateTime,
    stop: DateTime,
  ) => Promise<void>;
  clearTasks: () => void;
}

type ManagerTasksStore = ManagerTasksState & ManagerTasksActions;

type ManagerTasksStorePersist = Pick<
  ManagerTasksStore,
  'userTasks' | 'users' | 'selectedUserId' | 'weekStart'
>;

function convertToManagerTaskItem(
  task: RecurrenceTaskItem,
  recurrence: Recurrence,
  user: User,
): ManagerTaskItem {
  return {
    _id: task._id,
    recurrenceId: recurrence._id,
    title: recurrence.title,
    description: recurrence.description,
    start: task.start,
    stop: task.stop,
    status: task.status,
    rewardCard: recurrence.rewardCard,
    penaltyCard: recurrence.penaltyCard,
    assignedTo: user,
    isCompleted: task.isCompleted,
    icon: recurrence.icon,
  };
}

const useManagerTasksStore = create<ManagerTasksStore>()(
  persist<ManagerTasksStore, [], [], ManagerTasksStorePersist>(
    (set, get) => ({
      userTasks: [],
      users: [],
      selectedUserId: null,
      weekStart: DateTime.now().startOf('week'),
      isLoading: false,
      error: null,

      fetchTasks: async (userId?: string) => {
        const hasCachedTasks = get().userTasks.length > 0;
        set({ isLoading: !hasCachedTasks, error: null });

        try {
          const { weekStart } = get();
          const weekEnd = weekStart.endOf('week');

          const response = await api.tasksGetAll(
            weekStart,
            weekEnd,
            userId || get().selectedUserId || undefined,
          );

          const userTasksMap = new Map<string, UserTasks>();
          const usersMap = new Map<string, User>();

          response.forEach(({ recurrence, user, tasks: recurrenceTasks }) => {
            const userId = user._id;

            if (!usersMap.has(userId)) {
              usersMap.set(userId, user);
            }

            const tasksNeedingApproval = recurrenceTasks.map(task =>
              convertToManagerTaskItem(task, recurrence, user),
            );

            if (tasksNeedingApproval.length > 0) {
              if (!userTasksMap.has(userId)) {
                userTasksMap.set(userId, {
                  user,
                  tasks: [],
                });
              }

              const userTasks = userTasksMap.get(userId)!;
              userTasks.tasks.push(...tasksNeedingApproval);
            }
          });

          const userTasksProcessed = Array.from(userTasksMap.values());
          const allUsers = Array.from(usersMap.values());

          set({
            userTasks: userTasksProcessed,
            users: allUsers,
            isLoading: false,
          });
        } catch (error) {
          set({
            error:
              error instanceof Error ? error.message : 'Failed to fetch tasks',
            isLoading: false,
          });
        }
      },

      setSelectedUser: (userId: string | null) => {
        set({ selectedUserId: userId });
        get().fetchTasks(userId || undefined);
      },

      setWeek: (weekStart: DateTime) => {
        set({ weekStart });
        get().fetchTasks();
      },

      approveTask: async (taskId: string, status: RecurrenceTaskStatus) => {
        try {
          await api.approveTask(taskId, status);
          await get().fetchTasks();
        } catch (error) {
          throw new Error(
            error instanceof Error ? error.message : 'Failed to approve task',
          );
        }
      },

      updateTaskStatus: async (
        taskId: string,
        status: RecurrenceTaskStatus,
        recurrenceId: string,
        start: DateTime,
        stop: DateTime,
      ) => {
        try {
          await api.taskUpdateStatus(taskId, status, recurrenceId, start, stop);

          set(state => ({
            userTasks: state.userTasks.map(userTask => ({
              ...userTask,
              tasks: userTask.tasks.map(task =>
                task._id === taskId ? { ...task, status } : task,
              ),
            })),
          }));
        } catch (error) {
          throw new Error(
            error instanceof Error
              ? error.message
              : 'Failed to update task status',
          );
        }
      },

      clearTasks: () =>
        set({
          userTasks: [],
          users: [],
          selectedUserId: null,
          error: null,
        }),
    }),
    {
      name: 'manager-tasks-store',
      storage: createPersistStorage<ManagerTasksStorePersist>(),
      partialize: state => ({
        userTasks: state.userTasks,
        users: state.users,
        selectedUserId: state.selectedUserId,
        weekStart: state.weekStart,
      }),
      onRehydrateStorage: () => state => {
        if (!state) return;

        const currentWeekStart = DateTime.now().startOf('week');

        // Check if persisted weekStart is not the current week
        if (!state.weekStart.hasSame(currentWeekStart, 'day')) {
          state.weekStart = currentWeekStart;
          state.userTasks = [];
          state.users = [];
        }
      },
    },
  ),
);

export default useManagerTasksStore;
export type { ManagerTaskItem, UserTasks };
